import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import { Layout } from '@/components/custom/layout'
import {
  exportCustomers as exportCustomersApi,
  searchCustomers,
} from '@/api/customer'
import { SideFilter } from '@/components/customers-route/side-filter'
import { TopFilter } from '@/components/customers-route/top-filter'
import { DialogCustomer } from '@/components/customers-route/filter/dialog'
import CustomerListFilter from '@/components/customers-route/customer-list-filter'
import CustomerTable from '@/components/customers-route/table'
import { useCustomerListFilterStore } from '@/store/customer-list-filter-store'
import { useEffect, useMemo } from 'react'
import {
  buildBody,
  buildParams,
} from '@/components/customers-route/build-query'
import { getDistricts } from '@/api/settings'
import {
  getColumnOptions,
  getColumns,
} from '@/components/customers-route/table/columns'
import { throwException } from '@/exceptions/throw-exception.js'
import {
  useAllCities,
  useAllCustomerGroups,
  useAllFieldUser,
  useAllOrganizes,
  useUserConfig,
} from '@/hooks/use-setting-common.js'
import { isStringArray } from '@/utils/helper.js'
import { settingUserConfig } from '@/api/settings/common.js'
import { getUser } from '@/utils/auth.js'
import QUERY_KEY from '@/constants/use-query-key.js'
import { saveExcelFile } from '@/utils/export.js'
import { format } from 'date-fns'
import ContactPreview from '@/components/tickets-route/contact-preview'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useTabNavStore } from '@/store/tab-nav-store'
export const Route = createFileRoute('/_auth/customers/')({
  component: Customers,
})

function Customers() {
  const [
    assign,
    searchText,
    page,
    pageSize,
    filterModel,
    selectedColumnKeys,
    setSelectedColumnKeys,
    refreshField,
    quickFilter,
  ] = useCustomerListFilterStore(
    useShallow((state) => [
      state.assign,
      state.searchText,
      state.page,
      state.pageSize,
      state.filterModel,
      state.selectedColumnKeys,
      state.setSelectedColumnKeys,
      state.refreshField,
      state.quickFilter,
    ]),
  )

  const { href } = useLocation()
  const navigate = useNavigate()
  const addTab = useTabNavStore((s) => s.addTab)
  const setLastVisitedHref = useTabNavStore((s) => s.setLastVisitedHref)

  const handleNavigateDetail = (row) => {
    const customerId = row.original._id
    addTab(`/customers/${customerId}`, row.original.name)
    setLastVisitedHref(href)
    navigate({
      to: '/customers/$customerId',
      params: { customerId },
    })
  }
  const [isOpenPreviewContact, setIsOpenPreviewContact] = useState(false)
  const [dataContactPreview, setDataContactPreview] = useState(null)
  const queryClient = useQueryClient()
  const userConfig = useUserConfig()
  const { user_table_columns } = userConfig
  // Move the state update to useEffect
  useEffect(() => {
    if (user_table_columns) {
      if (JSON.stringify(selectedColumnKeys) !== user_table_columns) {
        const saveSelected = JSON.parse(user_table_columns)
        if (isStringArray(saveSelected)) {
          setSelectedColumnKeys(saveSelected)
        }
      }
    }
  }, [user_table_columns, selectedColumnKeys, setSelectedColumnKeys])

  const fetchCustomers = async () => {
    const params = buildParams()
    const body = buildBody({
      filterModel,
      assign,
      searchText,
      page,
      pageSize,
      quickFilter,
    })
    try {
      const resp = await searchCustomers({ params, data: body })
      return resp.data
    } catch (error) {
      throwException(error)
      return []
    }
  }

  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      QUERY_KEY.GET_LIST_CUSTOMER,
      {
        assign,
        filterModel,
        searchText,
        page,
        pageSize,
        quickFilter,
      },
    ],
    queryFn: fetchCustomers,
  })
  const customerFields = useAllFieldUser(refreshField)
  const groups = useAllCustomerGroups()
  const cities = useAllCities()
  const { data: organizes } = useAllOrganizes()

  const customers = isLoading ? [] : (queryData?.list ?? [])

  const { data: districts } = useQuery({
    initialData: [],
    queryKey: [
      QUERY_KEY.GET_ALL_DISTRICTS,
      {
        assign,
        filterModel,
        searchText,
        page,
        pageSize,
      },
    ],
    queryFn: async () => {
      if (customers.length > 0) {
        try {
          const { data } = await getDistricts({
            params: {
              districts: customers
                .filter((e) => e.district_id)
                ?.map((value) => value.district_id)
                .join(','),
            },
          })
          return data?.districts ?? []
        } catch (error) {
          throwException(error)
          return []
        }
      }
      return []
    },
    enabled: customers.length > 0,
  })
  const totalRecords = isLoading ? 0 : queryData?.total

  const columnOptions = getColumnOptions({ customerFields })
  const tableColumns = useMemo(() => {
    const columns = getColumns({
      districts,
      customerFields,
      organizes,
      groups,
      cities,
      handleNavigateDetail,
    })

    return columns.filter(
      (c) => selectedColumnKeys.indexOf(c.accessorKey) !== -1,
    )
  }, [organizes, groups, cities, customerFields, selectedColumnKeys])

  const exportCustomers = async () => {
    const params = buildParams()
    const body = buildBody({
      filterModel,
      assign,
      searchText,
      page,
      pageSize,
      quickFilter,
    })
    body.fields = tableColumns.map((value) => {
      if (value.accessorKey === 'organization') {
        value.accessorKey = 'organize_id'
      }
      return value.accessorKey
    })
    delete body.limit
    delete body.offset
    return await exportCustomersApi({ params, data: body })
  }

  const { isPending: isExporting, mutate: onExportClick } = useMutation({
    mutationFn: exportCustomers,
    onSuccess: (data) => {
      saveExcelFile(
        data,
        `Danh sách khách hàng ${format(new Date(), 'dd-MM-yyyy HH-mm')}`,
      )
    },
    onError: throwException,
  })

  const handleToggleColumn = async (column) => {
    const user = getUser()
    const columnIndex = selectedColumnKeys.indexOf(column.key)
    const newSelectedColumnKeys =
      columnIndex !== -1
        ? selectedColumnKeys.filter((key) => key !== column.key)
        : [...selectedColumnKeys, column.key]

    setSelectedColumnKeys(newSelectedColumnKeys)
    const { error } = await settingUserConfig({
      data: {
        user_id: user._id,
        user_table_columns: JSON.stringify(newSelectedColumnKeys),
      },
    })
    if (!error) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_USER_CONFIGURATION],
      })
    }
  }

  const handleOpenPreviewContact = async (data) => {
    setDataContactPreview(data)
    setIsOpenPreviewContact(true)
  }

  return (
    <Layout.Body className="py-0">
      <div className="flex h-full flex-row">
        <div className="h-full w-[200px] min-w-[200px] py-[--body-padding-y]">
          <SideFilter />
          <div className="flex w-full justify-between">
            <p className="pt-2 text-sm font-medium">Danh sách bộ lọc</p>
            <p className="pt-1">
              <DialogCustomer />
            </p>
          </div>
          <CustomerListFilter />
        </div>
        <div className="ml-3 w-[1px] bg-border" />
        <div className="ml-6 flex min-w-0 grow flex-col space-y-4 py-[--body-padding-y]">
          {isOpenPreviewContact && (
            <ContactPreview
              contact={dataContactPreview || {}}
              isOpenPreview={isOpenPreviewContact}
              setIsOpenPreview={setIsOpenPreviewContact}
            />
          )}
          <TopFilter
            isExporting={isExporting}
            columnOptions={columnOptions}
            selectedColumnKeys={selectedColumnKeys}
            handleToggleColumn={handleToggleColumn}
            handleExport={onExportClick}
          />

          <CustomerTable
            handleOpenPreviewContact={handleOpenPreviewContact}
            handleNavigateDetail={handleNavigateDetail}
            loading={isLoading}
            customers={customers}
            columns={tableColumns}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
          />
        </div>
      </div>
    </Layout.Body>
  )
}
